import PropTypes from 'prop-types';
import { Chip, Box } from '@mui/material';
import { useListContext, useGetMany } from 'react-admin';
import { useMemo, useLayoutEffect, useState } from 'react';
import { consumePendingFilterReferenceRecord } from '@/helpers/filters/filterReferenceSeed.js';

const EMPTY_SELECTED_IDS = [];

/**
 * Chips for ReferenceArrayFilter driven by filterValues (not Autocomplete internal state).
 */
export const ReferenceArrayFilterChips = ({ source, reference, optionText, optionValue, displayDeleteIcon, onRemove }) => {
    const { filterValues } = useListContext();
    const selectedIds = useMemo(() => {
        const ids = filterValues?.[source];
        return Array.isArray(ids) ? ids : EMPTY_SELECTED_IDS;
    }, [filterValues, source]);
    const [seededById, setSeededById] = useState({});

    useLayoutEffect(() => {
        const pending = consumePendingFilterReferenceRecord(source);
        if (pending?.id != null) {
            setSeededById(prev => ({ ...prev, [String(pending.id)]: pending }));
        }
    }, [selectedIds, source]);

    const { data: recordsFromApi } = useGetMany(reference, { ids: selectedIds }, { enabled: selectedIds.length > 0 });

    const records = useMemo(() => {
        const byId = new Map();
        [...Object.values(seededById), ...(recordsFromApi || [])].filter(Boolean).forEach(record => {
            const id = optionValue ? record[optionValue] : record.id;
            byId.set(String(id), record);
        });
        return selectedIds.map(id => byId.get(String(id))).filter(Boolean);
    }, [seededById, recordsFromApi, selectedIds, optionValue]);

    if (selectedIds.length === 0) {
        return null;
    }

    const getLabel = record => (typeof optionText === 'function' ? optionText(record) : record[optionText || 'name']);

    return (
        <Box className='reference-array-filter__chips'>
            {selectedIds.map(id => {
                const record = records.find(item => String(optionValue ? item[optionValue] : item.id) === String(id));
                const label = record ? getLabel(record) : String(id);

                return <Chip key={String(id)} label={label} size='small' onDelete={displayDeleteIcon ? () => onRemove(id) : undefined} />;
            })}
        </Box>
    );
};

ReferenceArrayFilterChips.propTypes = {
    source: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
    optionText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    optionValue: PropTypes.string,
    displayDeleteIcon: PropTypes.bool,
    onRemove: PropTypes.func.isRequired,
};
